import { INDUSTRIES, type AssessmentListRow, type CreateAssessment } from '@cyberscore/contracts';
import { ClipboardCheck, Plus, X } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api';
import { useListState, usePage, type ListSpec } from '../../lib/list';
import { href, navigate } from '../../lib/router';
import { DataView, type Column } from '../../ui/DataView';
import {
  BAND_FILTER,
  INDUSTRY_FILTER,
  ProgressCell,
  ScoreBlock,
  ScoreCell,
  STATUS_FILTER,
  StatusPill,
  when,
} from '../clients/parts';
import { useCanStart, useCreateAssessment } from './api';

export { industryLabel, STATUS_LABEL } from './labels';
import { industryLabel } from './labels';

const SPEC: ListSpec = {
  id: 'assessments',
  sort: 'updated',
  view: 'cards',
  filters: ['status', 'band', 'industry', 'customer'],
};

/**
 * The assessments this person can see: staff see the tenant's, everyone else the ones they are in. Cards
 * or a table, searched, filtered, sorted and paged on the server (task 60); ?customer= shows one client's.
 */
export function AssessmentsPage() {
  const list = useListState(SPEC);
  const page = usePage<AssessmentListRow>('/assessments', list.state);
  const canStart = useCanStart();
  const [creating, setCreating] = useState(false);
  const customer = list.state.filters.customer;
  const client = customer ? (page.data?.items[0]?.customer_name ?? customer) : null;

  const columns: Column<AssessmentListRow>[] = [
    {
      key: 'title',
      label: 'Assessment',
      sort: 'title',
      render: (a) => (
        <a href={href.assessment(a.id)} className="cell-title">
          {a.title}
        </a>
      ),
    },
    { key: 'business', label: 'Business', sort: 'business', render: (a) => (<>{a.customer_name}{a.environment && <div className="muted small">{a.environment}</div>}</>) }, // prettier-ignore
    { key: 'industry', label: 'Industry', wide: true, render: (a) => (<>{industryLabel(a.context.industry)}{a.context.country && <span className="muted"> · {a.context.country}</span>}</>) }, // prettier-ignore
    { key: 'score', label: 'CyberScore', sort: 'score', render: (a) => <ScoreCell s={a.score} /> },
    { key: 'progress', label: 'Progress', render: (a) => <ProgressCell s={a.score} /> },
    { key: 'status', label: 'Status', render: (a) => <StatusPill status={a.status} /> },
    {
      key: 'updated',
      label: 'Updated',
      sort: 'updated',
      wide: true,
      render: (a) => <span className="muted">{when(a.updated_at)}</span>,
    },
  ];

  return (
    <>
      <div className="page-head row-between">
        <div>
          <h1>{client ? client : 'Assessments'}</h1>
          <p>
            {client ? (
              <>Every assessment for this client. <a href={href.assessments()}>Show all assessments</a></>
            ) : canStart ? (
              'Start an assessment for a business, then add the people who will answer it.'
            ) : (
              'The cyber assessments you have been asked to complete.'
            )}
          </p>
        </div>
        {canStart && !creating && (
          <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
            <Plus size={16} /> New assessment
          </button>
        )}
      </div>

      {creating && <NewAssessment onClose={() => setCreating(false)} client={customer ? { customer, customer_name: page.data?.items[0]?.customer_name ?? '' } : null} />}

      <DataView
        list={list}
        query={page}
        noun="assessments"
        rowKey={(a) => a.id}
        rowHref={(a) => href.assessment(a.id)}
        columns={columns}
        card={(a) => <AssessmentCard a={a} />}
        filters={[STATUS_FILTER, BAND_FILTER, INDUSTRY_FILTER]}
        sorts={[
          { value: 'updated', label: 'Recent activity', dir: 'desc' },
          { value: 'created', label: 'Newest', dir: 'desc' },
          { value: 'score', label: 'CyberScore', dir: 'asc' },
          { value: 'business', label: 'Business', dir: 'asc' },
          { value: 'title', label: 'Title', dir: 'asc' },
        ]}
        searchLabel="Search assessments or businesses"
        empty={<><ClipboardCheck size={28} /><strong>No assessments yet</strong>{canStart ? 'Start one with New assessment.' : 'You will see one here once you are added to it.'}</>}
      />
    </>
  ); // prettier-ignore
}

function AssessmentCard({ a }: { a: AssessmentListRow }) {
  return (
    <article className="ccard">
      <div className="ccard-head">
        <div className="minw">
          <h3><a className="cell-title" href={href.assessment(a.id)}>{a.customer_name}</a></h3>
          <div className="muted small">{a.environment && `${a.environment} · `}{a.title} · {industryLabel(a.context.industry)}</div>
        </div>
        <StatusPill status={a.status} />
      </div>
      <ScoreBlock s={a.score} />
      <div className="ccard-stats">
        <div><b className={a.score?.exceptions ? 'bad-t' : undefined}>{a.score ? a.score.exceptions : '–'}</b><span>exceptions</span></div>
        <div><b>{a.score ? `${Math.round(a.score.evidence_confidence * 100)}%` : '–'}</b><span>verified</span></div>
      </div>
      <div className="ccard-foot">
        {a.score ? <a href={href.score(a.id)}>See the score</a> : <span className="muted">Started {when(a.created_at)}</span>}
        <span>{when(a.updated_at)}</span>
      </div>
    </article>
  ); // prettier-ignore
}

/** Started from one client's assessments, it is for that client (task 66). */
function NewAssessment({
  onClose,
  client,
}: {
  onClose: () => void;
  client: { customer: string; customer_name: string } | null;
}) {
  // prettier-ignore
  const create = useCreateAssessment();
  const [form, setForm] = useState({
    title: '',
    customer_name: client?.customer_name ?? '',
    customer: client?.customer ?? '',
    country: 'MY',
    industry: 'manufacturing',
  });
  const [issues, setIssues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  // The customer key defaults to a slug of the business name until it is edited.
  const [keyEdited, setKeyEdited] = useState(!!client);
  const slug = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 60);
  const set = (k: keyof typeof form, v: string) =>
    setForm((f) => ({
      ...f,
      [k]: v,
      ...(k === 'customer_name' && !keyEdited ? { customer: slug(v) } : {}),
    }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setIssues({});
    setError(null);
    const body: CreateAssessment = { ...form, country: form.country.toUpperCase() };
    try {
      const a = await create.mutateAsync(body);
      navigate(href.assessment(a.id));
    } catch (err) {
      if (err instanceof ApiError && err.issues.length) {
        setIssues(Object.fromEntries(err.issues.map((i) => [i.path.split('.')[0]!, i.message])));
        if (!err.issues.some((i) => i.path in form)) setError(err.message);
      } else setError(err instanceof Error ? err.message : 'Could not start the assessment');
    }
  };

  const field = (k: keyof typeof form, label: string, input: React.ReactNode, hint?: string) => (
    <div className="field">
      <label htmlFor={`na-${k}`}>{label}</label>
      {input}
      {issues[k] ? <span className="error">{issues[k]}</span> : hint && <span className="hint">{hint}</span>}
    </div>
  );
  const text = (k: keyof typeof form, extra: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <input
      id={`na-${k}`}
      className={`input${issues[k] ? ' invalid' : ''}`}
      value={form[k]}
      onChange={(e) => set(k, e.target.value)}
      {...extra}
    />
  );

  return (
    <form className="card form-card" onSubmit={submit} noValidate>
      <div className="card-head">
        <div className="card-title">New assessment</div>
        <button type="button" className="btn btn-icon btn-ghost" aria-label="Close" onClick={onClose}>
          <X size={16} />
        </button>
      </div>
      <div className="form-body">
        {field('customer_name', 'Business name', text('customer_name', { required: true, autoFocus: true }))}
        {field('title', 'Assessment title', text('title', { required: true, placeholder: 'e.g. FY27 renewal' }))}
        <div className="two">
          {field(
            'industry',
            'Industry',
            <select id="na-industry" className="input" value={form.industry} onChange={(e) => set('industry', e.target.value)}>
              {Object.entries(INDUSTRIES).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>,
            'Decides which journey and industry controls apply.',
          )}
          {field('country', 'Country', text('country', { maxLength: 2 }), 'Two letters, e.g. MY')}
        </div>
        {field(
          'customer',
          'Customer reference',
          <input
            id="na-customer"
            className={`input mono${issues.customer ? ' invalid' : ''}`}
            value={form.customer}
            onChange={(e) => {
              setKeyEdited(true);
              set('customer', e.target.value);
            }}
          />,
          'How your systems refer to this business. Customer-specific configuration uses it.',
        )}
        {error && <div className="notice">{error}</div>}
      </div>
      <div className="form-foot">
        <button type="button" className="btn" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={create.isPending}>
          {create.isPending ? 'Starting…' : 'Start assessment'}
        </button>
      </div>
    </form>
  ); // prettier-ignore
}
